import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Calendar,
  FileText,
  MapPin,
  LogOut,
  Sparkles,
  Loader2,
  Award,
  Gift,
  Download,
  KeyRound,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import QuickRequestModal from "@/components/forms/QuickRequestModal";
import PasswordModal from "@/components/auth/PasswordModal";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, loading, logout, authHeader, API } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [leadsRes, rewardsRes] = await Promise.all([
        axios.get(`${API}/leads/me`, {
          headers: authHeader(),
          withCredentials: true,
        }),
        axios.get(`${API}/me/rewards`, {
          headers: authHeader(),
          withCredentials: true,
        }),
      ]);
      setLeads(leadsRes.data);
      setRewards(rewardsRes.data);
    } catch (e) {
      console.error(e);
      setError("Could not load your history.");
    }
  }, [API, authHeader]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    fetchAll();
  }, [user, loading, navigate, fetchAll]);

  const downloadDeliverable = async (file) => {
    try {
      const { data } = await axios.get(`${API}/me/deliverables/${file.id}`, {
        headers: authHeader(),
        withCredentials: true,
      });
      const a = document.createElement("a");
      a.href = data.data;
      a.download = data.filename || file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error(e);
      toast.error("Could not download file.");
    }
  };

  if (loading || (user && leads === null && !error)) {
    return (
      <main className="min-h-screen bg-[#FAF7F2]" data-testid="account-loading">
        <Nav />
        <div className="pt-40 flex items-center justify-center text-stone-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      </main>
    );
  }

  if (!user) return null;

  return (
    <main className="min-h-screen bg-[#FAF7F2] text-stone-900" data-testid="account-root">
      <Nav />

      <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 px-6 lg:px-12 bg-bubbly relative overflow-hidden">
        <div className="blob blob-teal w-[420px] h-[420px] -top-32 -left-32" />
        <div className="blob blob-peach w-[320px] h-[320px] top-1/3 -right-24" />

        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/"
            data-testid="account-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-stone-900 mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                Your account
              </span>
              <h1 className="font-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.02]">
                Hi, {user.name?.split(" ")[0] || "friend"}.
              </h1>
              <p className="mt-4 text-stone-600 text-lg leading-relaxed max-w-xl">
                Every plan you&apos;ve requested lives here. Re-order, revisit
                files, or pick up where you left off.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-white border border-stone-200 rounded-2xl p-5">
                <div className="text-xs text-stone-500 mb-1">Signed in as</div>
                <div className="flex items-center gap-2 text-stone-900 font-semibold mb-3 break-all">
                  <Mail className="h-4 w-4 text-teal-700 flex-shrink-0" /> {user.email}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setPwOpen(true)}
                    data-testid="account-change-password-btn"
                    className="inline-flex items-center gap-2 bg-white border border-stone-200 text-stone-800 px-4 py-2 rounded-full text-xs font-semibold hover:border-teal-700 hover:text-teal-700 transition-colors"
                  >
                    <KeyRound className="h-3.5 w-3.5" /> Change password
                  </button>
                  <button
                    onClick={async () => {
                      await logout();
                      toast.message("Signed out");
                      navigate("/");
                    }}
                    data-testid="account-signout-btn"
                    className="inline-flex items-center gap-2 bg-stone-900 text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-teal-700 transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-10 px-6 lg:px-12" data-testid="account-rewards-section">
        <div className="mx-auto max-w-7xl">
          {rewards && (
            <RewardsCard rewards={rewards} />
          )}
        </div>
      </section>

      {rewards && rewards.deliverables && rewards.deliverables.length > 0 && (
        <section className="pb-10 px-6 lg:px-12" data-testid="account-deliverables-section">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-6">
              Your delivered plans
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rewards.deliverables.map((d) => (
                <DeliverableCard
                  key={d.id}
                  file={d}
                  onDownload={() => downloadDeliverable(d)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="pb-24 lg:pb-32 px-6 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Your plan history
            </h2>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              data-testid="account-new-request-btn"
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
            >
              + New request
            </button>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl px-5 py-4 text-sm text-rose-800" data-testid="account-error">
              {error}
            </div>
          )}

          {!error && leads && leads.length === 0 && (
            <div
              className="bg-white border border-stone-200 rounded-[2rem] p-10 text-center"
              data-testid="account-empty"
            >
              <div className="inline-flex h-12 w-12 rounded-full bg-teal-100 items-center justify-center mb-4">
                <FileText className="h-5 w-5 text-teal-700" />
              </div>
              <h3 className="font-display text-xl font-semibold">
                No plans yet
              </h3>
              <p className="text-stone-600 mt-2 max-w-md mx-auto">
                Submit a quote request and your projects will appear here.
              </p>
              <Link
                to="/#contact"
                className="mt-6 inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
              >
                Request your first plan
              </Link>
            </div>
          )}

          {!error && leads && leads.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {leads.map((lead) => (
                <LeadCard key={lead.id} lead={lead} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <QuickRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        prefill={{ name: user.name, email: user.email }}
        authHeader={authHeader}
        onSuccess={() => {
          fetchAll();
        }}
      />
      <PasswordModal open={pwOpen} onClose={() => setPwOpen(false)} mode="self" />
    </main>
  );
}

const TINTS = {
  "2D Floor Plan": "bg-teal-100 text-teal-800",
  "3D Floor Plan": "bg-amber-100 text-amber-800",
  "Renew Existing Floor Plan": "bg-sky-100 text-sky-800",
  Other: "bg-stone-100 text-stone-800",
};

function LeadCard({ lead }) {
  const tint = TINTS[lead.service_type] || TINTS.Other;
  const created = new Date(lead.created_at);
  const dateStr = created.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div
      className="bg-white border border-stone-200 rounded-[2rem] p-6 hover:shadow-lg transition-shadow"
      data-testid={`account-lead-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className={`inline-block ${tint} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider`}>
          {lead.service_type}
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-stone-500">
          <Calendar className="h-3.5 w-3.5" /> {dateStr}
        </span>
      </div>

      {lead.style && (
        <div className="text-xs text-stone-500 mb-3">
          Style: <span className="font-semibold text-stone-700">{lead.style}</span>
        </div>
      )}

      <h3 className="font-display text-lg font-semibold leading-snug">
        {lead.property_address || "No address provided"}
      </h3>
      {lead.property_address && (
        <div className="mt-1 text-xs text-stone-500 inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" /> Property
        </div>
      )}
      {lead.estimated_sqft != null && (
        <div className="mt-2 inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
          ~ {Number(lead.estimated_sqft).toLocaleString()} sq ft
        </div>
      )}

      {lead.urgency && (
        <div className={`mt-2 ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
          (lead.urgency === "within_48h" || lead.urgency === "within_24h")
            ? "bg-rose-100 text-rose-800"
            : "bg-sky-100 text-sky-800"
        }`}>
          {lead.urgency === "within_48h"
            ? "Within 48h"
            : lead.urgency === "within_24h"
            ? "Within 24h"
            : "Within 1 week"}
        </div>
      )}

      {lead.message && (
        <p className="mt-3 text-sm text-stone-600 leading-relaxed line-clamp-3">
          {lead.message}
        </p>
      )}

      <div className="mt-4 pt-4 border-t border-stone-100 text-xs">
        <span className="text-stone-500">Submitted as {lead.email}</span>
      </div>
    </div>
  );
}


function RewardsCard({ rewards }) {
  const goal = rewards.points_per_free_credit || 500;
  const earn = rewards.points_per_paid_lead || 100;
  const pct = Math.min(100, Math.round((rewards.points / goal) * 100));
  const toNext = Math.max(0, goal - rewards.points);

  return (
    <div
      className="bg-white border border-stone-200 rounded-[2rem] p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8"
      data-testid="account-rewards-card"
    >
      <div className="lg:col-span-7">
        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
          <Award className="h-3.5 w-3.5" /> Rewards
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span
            className="font-display text-5xl sm:text-6xl font-semibold tracking-tight"
            data-testid="rewards-points-balance"
          >
            {rewards.points}
          </span>
          <span className="text-stone-500 text-base">/ {goal} pts</span>
        </div>
        <p className="mt-2 text-sm text-stone-600 max-w-md">
          Every paid floor plan earns you <strong>{earn} points</strong>. Reach{" "}
          <strong>{goal}</strong> to unlock <strong>1 free B&amp;W floor plan</strong>.
        </p>

        <div className="mt-5 h-3 w-full bg-stone-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-amber-500 rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
            data-testid="rewards-progress-bar"
          />
        </div>
        <div className="mt-2 text-xs text-stone-500">
          {toNext === 0 ? (
            <span className="text-teal-700 font-semibold">
              Free plan unlocked — we&apos;ll be in touch!
            </span>
          ) : (
            <>
              <strong className="text-stone-700">{toNext}</strong> more points to your next free plan
            </>
          )}
        </div>
        <div className="mt-3 text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
          Lifetime earned: <span className="text-stone-700">{rewards.lifetime_points} pts</span>
        </div>
      </div>

      <div className="lg:col-span-5 grid grid-cols-2 gap-3">
        <StatTile
          tint="teal"
          label="Free credits"
          value={rewards.free_credits}
          icon={<Gift className="h-4 w-4" />}
          testId="rewards-free-credits"
          sub={rewards.free_credits > 0 ? "Ready to redeem" : "Earn 500 pts"}
        />
        <StatTile
          tint="stone"
          label="Credits used"
          value={rewards.free_credits_used}
          icon={<Sparkles className="h-4 w-4" />}
          testId="rewards-credits-used"
          sub="Free plans claimed"
        />
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, icon, tint = "stone", testId }) {
  const map = {
    stone: "bg-stone-50 border-stone-200 text-stone-900",
    teal: "bg-teal-50 border-teal-200 text-teal-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div className={`border rounded-2xl p-4 ${map[tint]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider opacity-70 font-semibold">
        {icon} {label}
      </div>
      <div className="font-display text-3xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-[11px] mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

function DeliverableCard({ file, onDownload }) {
  const uploaded = new Date(file.uploaded_at);
  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl p-5"
      data-testid={`deliverable-${file.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="inline-flex h-10 w-10 rounded-xl bg-teal-100 items-center justify-center flex-shrink-0">
          <FileText className="h-5 w-5 text-teal-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-stone-900 truncate" title={file.filename}>
            {file.filename}
          </div>
          {file.note && (
            <div className="text-xs text-stone-600 mt-1 line-clamp-2">{file.note}</div>
          )}
          <div className="text-[11px] text-stone-500 mt-2 inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {uploaded.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onDownload}
        data-testid={`deliverable-download-${file.id}`}
        className="mt-4 w-full inline-flex items-center justify-center gap-1.5 bg-stone-900 text-white px-3 py-2 rounded-full text-xs font-semibold hover:bg-teal-700 transition-colors"
      >
        <Download className="h-3.5 w-3.5" /> Download
      </button>
    </div>
  );
}
